import type {
  FieldRule,
  RuleCondition,
  RuleAction,
  RuleLogicAction,
  RuleCalculationAction,
  RuleOperator,
  CalculationOperation,
  FormField,
} from '../types/forms';

// =====================================================
// ID GENERATION
// =====================================================

export function generateRuleId(): string {
  return `rule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function generateConditionId(): string {
  return `cond_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
}

// =====================================================
// FACTORY FUNCTIONS
// =====================================================

export function createDefaultCondition(fieldId: string): RuleCondition {
  return {
    id: generateConditionId(),
    fieldId,
    operator: 'is',
    value: '',
  };
}

export function createDefaultJumpAction(targetFieldId: string): RuleLogicAction {
  return { type: 'jump_to', targetFieldId };
}

export function createDefaultCalcAction(
  operation: CalculationOperation,
  variableId: string
): RuleCalculationAction {
  return { type: 'calculation', operation, value: 0, variableId };
}

export function createDefaultRule(sourceFieldId: string): FieldRule {
  return {
    id: generateRuleId(),
    conditionType: 'if',
    conditions: [createDefaultCondition(sourceFieldId)],
    logicOperator: 'and',
    actions: [createDefaultJumpAction('')],
  };
}

// =====================================================
// OPERATOR MAPPING (Rule ↔ Condition)
// =====================================================

export function ruleOperatorToConditionOperator(op: RuleOperator): 'equals' | 'not_equals' {
  return op === 'is' ? 'equals' : 'not_equals';
}

export function conditionOperatorToRuleOperator(op: string): RuleOperator {
  return op === 'not_equals' || op === 'is_not' ? 'is_not' : 'is';
}

// =====================================================
// MIGRATION: ConditionalLogic → FieldRule[]
// =====================================================

export function migrateConditionalLogicToRules(field: FormField): FieldRule[] {
  const logic = field.conditionalLogic;
  if (!logic) return [];

  const rules: FieldRule[] = [];

  if (logic.action === 'jump_to') {
    // Multi-branch: per-condition jumpTo
    if (logic.conditions?.length) {
      for (const cond of logic.conditions) {
        if (cond.jumpTo) {
          rules.push({
            id: generateRuleId(),
            conditionType: 'if',
            conditions: [{
              id: generateConditionId(),
              fieldId: cond.fieldId,
              operator: conditionOperatorToRuleOperator(cond.operator),
              value: cond.value || '',
            }],
            logicOperator: 'and',
            actions: [{ type: 'jump_to', targetFieldId: cond.jumpTo }],
          });
        }
      }
    }
    // Single branch: field-level jumpTo
    else if (logic.jumpTo) {
      const hasCondition = !!logic.dependsOn;
      rules.push({
        id: generateRuleId(),
        conditionType: hasCondition ? 'if' : 'always',
        conditions: hasCondition ? [{
          id: generateConditionId(),
          fieldId: logic.dependsOn!,
          operator: conditionOperatorToRuleOperator(logic.operator || 'equals'),
          value: logic.value || '',
        }] : [],
        logicOperator: 'and',
        actions: [{ type: 'jump_to', targetFieldId: logic.jumpTo }],
      });
    }
  }

  return rules;
}

// =====================================================
// CLEANUP: Remove orphan references
// =====================================================

export function cleanRulesOnFieldDelete(
  rules: FieldRule[],
  deletedFieldId: string
): FieldRule[] {
  return rules
    .map(rule => ({
      ...rule,
      conditions: rule.conditions.filter(c => c.fieldId !== deletedFieldId),
      actions: rule.actions.filter(a =>
        a.type !== 'jump_to' || a.targetFieldId !== deletedFieldId
      ),
    }))
    .filter(rule => rule.actions.length > 0)
    // Remove conditional rules that lost all their conditions (would execute unconditionally)
    .filter(rule => rule.conditionType !== 'if' || rule.conditions.length > 0);
}

export function cleanRulesOnVariableDelete(
  rules: FieldRule[],
  deletedVariableId: string
): FieldRule[] {
  return rules
    .map(rule => ({
      ...rule,
      actions: rule.actions.filter(a =>
        a.type !== 'calculation' || a.variableId !== deletedVariableId
      ),
    }))
    .filter(rule => rule.actions.length > 0);
}
